import { logger } from '../../utils/logger';
import React, { useEffect, useState } from 'react';
import { useMsalAuth } from '../../hooks/useMsalAuth';

/**
 * Account Suspended Page
 *
 * Displayed when a user's account has been suspended.
 * Shows error message and allows user to contact support or sign out.
 */
const AccountSuspendedPage: React.FC = () => {
  const { logout } = useMsalAuth();
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    // Get error message from session storage
    const message = sessionStorage.getItem('auth_error_message');
    if (message) {
      setErrorMessage(message);
      // Clear the message after reading
      sessionStorage.removeItem('auth_error_message');
    }
  }, []);

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      logger.error('Logout failed:', error);
      // Force redirect on failure
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Warning Icon */}
        <div className="flex justify-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-12 h-12 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">Account Suspended</h2>
          <p className="mt-4 text-gray-600">
            {errorMessage ||
              'Your account has been suspended. Please contact support for assistance.'}
          </p>
        </div>

        {/* Support Info */}
        <div className="bg-gray-100 rounded-lg p-6 text-center">
          <p className="text-sm text-gray-600">
            If you believe this is an error, please contact our support team:
          </p>
          <a
            href="mailto:support@mindalyx.com"
            className="mt-2 inline-block text-primary-600 hover:text-primary-700 font-medium"
          >
            support@mindalyx.com
          </a>
        </div>

        {/* Sign Out Button */}
        <div>
          <button
            onClick={handleSignOut}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
          >
            Sign Out
          </button>
        </div>

        {/* Additional Info */}
        <div className="text-center text-xs text-gray-500">
          <p>Your session has been terminated for security reasons.</p>
        </div>
      </div>
    </div>
  );
};

export default AccountSuspendedPage;
